import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/apiClient'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SupportLine, TermsConsent } from '@/components/ui/TermsConsent'
import { termsVersion } from '@/lib/legal'
import { ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import { NativeSelect } from '@/components/ui/NativeSelect'
import { QuantityInput } from '@/components/ui/QuantityInput'
import { formatMoney } from '@/lib/format'
import { taxIdPattern } from '@/lib/licenses'

type Company = { id: string; businessName: string; taxId?: string | null }
type TimbrePackage = { productCode: string; productName: string; creditQuantity: number; amountCents: number; currency: string }

export default function TimbreCheckoutPage() {
  const [search] = useSearchParams()
  const requestedCompany = search.get('companyId') ?? ''
  const [companies, setCompanies] = useState<Company[]>([])
  const [packages, setPackages] = useState<TimbrePackage[]>([])
  const [companyId, setCompanyId] = useState('')
  const [productCode, setProductCode] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [taxId, setTaxId] = useState('')
  const [taxIdChecked, setTaxIdChecked] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [error, setError] = useState('')
  const idempotencyKey = useRef(crypto.randomUUID())
  const taxIdInput = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [availableCompanies, availablePackages] = await Promise.all([api.get<Company[]>('/companies'), api.get<TimbrePackage[]>('/timbres/packages')])
      setCompanies(availableCompanies); setPackages(availablePackages)
      setCompanyId(availableCompanies.some((company) => company.id === requestedCompany) ? requestedCompany : availableCompanies[0]?.id ?? '')
      setProductCode(availablePackages[0]?.productCode ?? '')
    } catch (cause) { setError(cause instanceof ApiError ? cause.message : 'No pudimos cargar los paquetes de timbres.') }
    finally { setLoading(false) }
  }, [requestedCompany])

  useEffect(() => { void load() }, [load])
  const selected = packages.find((item) => item.productCode === productCode)
  const total = selected ? selected.amountCents * quantity : 0
  // The stamp account at SW is opened with the company's RFC: asked once, while the company has none.
  const company = companies.find((item) => item.id === companyId)
  const needsTaxId = Boolean(company) && !company?.taxId
  const taxIdValid = taxIdPattern.test(taxId.trim())
  const taxIdError = needsTaxId && taxIdChecked && !taxIdValid
    ? (taxId.trim() ? 'El RFC no es válido: 12 o 13 caracteres, como ABC010101AB1.' : 'Escribe el RFC de la empresa.')
    : ''

  function review() {
    setTaxIdChecked(true)
    if (needsTaxId && !taxIdValid) { taxIdInput.current?.focus(); return }
    setTermsAccepted(false); setConfirmOpen(true)
  }

  async function submit() {
    if (!companyId || !selected) { setError('Selecciona una empresa y un paquete de timbres.'); return }
    setSaving(true); setError('')
    try {
      const body = needsTaxId ? { companyId, productCode, quantity, termsVersion, taxId: taxId.trim() } : { companyId, productCode, quantity, termsVersion }
      const response = await api.post<{ checkoutUrl: string }>('/timbres/checkout', body, { 'Idempotency-Key': idempotencyKey.current })
      window.location.assign(response.checkoutUrl)
    } catch (cause) { setError(cause instanceof ApiError ? cause.message : 'No pudimos iniciar el checkout de timbres.') }
    finally { setSaving(false) }
  }

  if (loading) return <section className="portal-page"><LoadingState label="Consultando paquetes de timbres…" /></section>
  if (error && packages.length === 0) return <section className="portal-page"><ErrorState title={error} retry={() => void load()} /></section>
  return <section className="portal-page software-checkout-page">
    <header className="flow-heading"><Link to={companyId ? `/empresas/${companyId}/licencias` : '/empresas'} className="back-link">← Licencias</Link><p className="eyebrow">Facturación electrónica</p><h1>Comprar timbres</h1><p>Los timbres se acreditan en la cuenta de timbrado de tu empresa y tu tienda los usa al facturar.</p></header>
    {error && <Notice>{error}</Notice>}
    {packages.length === 0 ? <ErrorState title="Aún no hay paquetes publicados" retry={() => void load()} /> : <div className="portal-form">
      <NativeSelect label="Empresa" value={companyId} onChange={(event) => { setCompanyId(event.target.value); setTaxIdChecked(false) }}><option value="">Selecciona una empresa</option>{companies.map((item) => <option key={item.id} value={item.id}>{item.businessName}</option>)}</NativeSelect>
      {needsTaxId && <div className="form-field tax-id-field">
        <label htmlFor="timbre-tax-id">RFC de la empresa</label>
        <input id="timbre-tax-id" ref={taxIdInput} value={taxId} maxLength={13} autoComplete="off" spellCheck={false} autoCapitalize="characters"
          aria-invalid={taxIdError ? true : undefined} aria-describedby={taxIdError ? 'timbre-tax-id-hint timbre-tax-id-error' : 'timbre-tax-id-hint'}
          onChange={(event) => setTaxId(event.target.value.toUpperCase().replace(/\s+/g, ''))} />
        <p id="timbre-tax-id-hint" className="field-hint">Con él abrimos la cuenta de timbrado de tu negocio en SW.</p>
        {taxIdError && <p id="timbre-tax-id-error" className="field-error" role="alert">{taxIdError}</p>}
      </div>}
      <NativeSelect label="Paquete" value={productCode} onChange={(event) => setProductCode(event.target.value)}>{packages.map((item) => <option key={item.productCode} value={item.productCode}>{item.productName} · {item.creditQuantity.toLocaleString('es-MX')} timbres</option>)}</NativeSelect>
      {selected && <div className="license-line"><div><strong>{selected.productName}</strong><p>Se acreditan {selected.creditQuantity.toLocaleString('es-MX')} timbres por paquete después de la confirmación de Stripe.</p></div><div className="license-line-controls"><span>{formatMoney(selected.amountCents, selected.currency)}</span><QuantityInput label="Paquetes" value={quantity} min={1} max={1000} onChange={setQuantity} disabled={saving} /></div></div>}
      <div className="license-total"><div><span>Total estimado</span><small>Pago único; no se crea una suscripción.</small></div><strong>{formatMoney(total, selected?.currency ?? 'MXN')}</strong></div>
      <Button type="button" onClick={review} disabled={!companyId || !selected || saving}>Continuar al pago</Button>
    </div>}
    <ConfirmDialog open={confirmOpen} title="Revisar compra de timbres" action="Continuar con Stripe" busy={saving} disabled={!termsAccepted} error={saving ? undefined : error || undefined} onCancel={() => { if (!saving) setConfirmOpen(false) }} onConfirm={() => void submit()}><p>Vas a abrir Stripe para confirmar {formatMoney(total, selected?.currency ?? 'MXN')} por <strong>{(selected?.creditQuantity ?? 0) * quantity} timbres</strong>. Es un pago único.</p><SupportLine /><TermsConsent checked={termsAccepted} disabled={saving} onChange={setTermsAccepted} /></ConfirmDialog>
  </section>
}
